import { useState, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { FilterDialog } from './FilterDialog.tsx'
import styles from './FilterBar.module.css'

// One multi-select facet: a trigger pill that opens a FilterDialog of options.
// Values are plain strings at this boundary (enums collapse to string); the
// caller casts in its onToggle.
export type FilterFacet = {
	key: string
	label: string
	options: ReadonlyArray<{ value: string; label: string; emoji?: string }>
	selected: ReadonlyArray<string>
	onToggle: (value: string) => void
	onClear: () => void
}

type Props = {
	facets: ReadonlyArray<FilterFacet>
	// Bespoke trigger pills rendered before / after the facets (e.g. the
	// activity Distance/Where controls). Their own dialogs, if any, are the
	// caller's.
	leading?: ReactNode
	extra?: ReactNode
	activeCount: number
	onClearAll: () => void
}

// Sticky horizontal pill bar shared by the activity and recipe pages. Owns the
// open-dialog state for its facets; everything else is driven by props.
export const FilterBar = ({
	facets,
	leading,
	extra,
	activeCount,
	onClearAll,
}: Props) => {
	const [open, setOpen] = useState<string | null>(null)

	const triggerClass = (active: boolean): string =>
		`${styles.trigger} ${active ? styles.triggerActive : ''}`.trim()

	return (
		<div className={styles.bar}>
			<div className={styles.scroll}>
				{activeCount > 0 ? (
					<button
						type="button"
						className={styles.clearAll}
						aria-label="Clear all filters"
						onClick={onClearAll}
					>
						<X size={16} aria-hidden="true" />
					</button>
				) : null}

				{leading}

				{facets.map((f) => (
					<button
						key={f.key}
						type="button"
						className={triggerClass(f.selected.length > 0)}
						onClick={() => setOpen(f.key)}
					>
						{f.selected.length > 0
							? `${f.label} (${f.selected.length})`
							: f.label}
					</button>
				))}

				{extra}
			</div>

			{facets.map((f) => (
				<FilterDialog
					key={f.key}
					open={open === f.key}
					title={f.label}
					options={f.options}
					selected={f.selected}
					onToggle={f.onToggle}
					onClear={f.onClear}
					onClose={() => setOpen(null)}
				/>
			))}
		</div>
	)
}
